import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Edit3, Loader2, Bot, Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as Sentry from '@sentry/react';
import ProtocolAiAssistant from "./ProtocolAiAssistant";

interface JsonEditorModalProps {
  initialJson: object;
  title: string;
  renderPreview: (jsonData: object) => React.ReactNode;
  experimentId?: string;
  onSave?: () => void;
}

const JsonEditorModal: React.FC<JsonEditorModalProps> = ({
  initialJson,
  title,
  renderPreview,
  experimentId,
  onSave
}) => {
  const [jsonString, setJsonString] = useState(JSON.stringify(initialJson, null, 2));
  const [isOpen, setIsOpen] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState("Copy JSON");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [parsedJson, setParsedJson] = useState<object>(initialJson);

  const saveMutation = useMutation({
    mutationFn: async (protocolData: object) => {
      if (!experimentId) {
        throw new Error("Experiment ID is required to save");
      }

      const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'PUT',
          endpoint: `/api/v2/experiments/${experimentId}`,
          body: {
            experiment_protocol: protocolData
          }
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to save protocol');
      }

      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to save protocol'}`);
      }

      return apiResponse.data;
    },
    onSuccess: () => {
      toast.success("Experiment protocol saved successfully");
      setIsOpen(false);
      onSave?.(); // Refresh the experiment query
    },
    onError: (error) => {
      toast.error(`Failed to save protocol: ${error.message}`);
              Sentry.captureException(error);
    },
  });

  const handleJsonChange = (value: string) => {
    setJsonString(value);
    try {
      const parsed = JSON.parse(value);
      setJsonError(null);
      setParsedJson(parsed);
    } catch (error) {
      setJsonError("Invalid JSON format");
      setParsedJson(initialJson); // Keep showing the last valid JSON in preview
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopyButtonText("Copied..");
      setTimeout(() => setCopyButtonText("Copy JSON"), 2000);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleSave = () => {
    if (jsonError) {
      toast.error("Cannot save invalid JSON. Please fix the syntax errors first.");
      return;
    }
    saveMutation.mutate(parsedJson);
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      setParsedJson(parsed);
      setJsonError(null);
      toast.success("JSON formatted successfully");
    } catch (error) {
      toast.error("Cannot format invalid JSON. Please fix syntax errors first.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 ml-2 opacity-60 hover:opacity-100"
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit {title}
            <ProtocolAiAssistant
              currentJson={parsedJson}
              onJsonUpdate={(newJson) => {
                const newJsonString = JSON.stringify(newJson, null, 2);
                setJsonString(newJsonString);
                setParsedJson(newJson);
                setJsonError(null);
              }}
            />
          </DialogTitle>
          {experimentId ? (
            <p className="text-xs text-muted-foreground mt-1">
              Edit the JSON content and click Save to update the experiment protocol.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              This editor is for preview and copy only. Changes here are not saved; copy the JSON and update it in the database.
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-hidden min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            <div className="flex flex-col min-h-0">
              <h3 className="text-sm font-medium mb-2">JSON Editor</h3>
              <ScrollArea className="flex-1 min-h-0 border rounded-md">
                <Textarea
                  value={jsonString}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  className="min-h-[500px] w-full font-mono text-sm resize-none border-0 focus-visible:ring-0"
                  placeholder="Enter your JSON here..."
                />
              </ScrollArea>
              {jsonError && (
                <p className="text-sm text-red-500 mt-1">{jsonError}</p>
              )}
            </div>
            <div className="flex flex-col min-h-0">
              <h3 className="text-sm font-medium mb-2">Preview</h3>
              <ScrollArea className="h-[500px] border rounded-md bg-white">
                <div className="p-2">
                  {renderPreview(parsedJson)}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleFormat}>
            <Sparkles className="h-4 w-4 mr-2" />
            Format JSON
          </Button>
          <Button variant="outline" onClick={handleCopy}>
            {copyButtonText}
          </Button>
          {experimentId && (
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || !!jsonError}
              className="min-w-[80px]"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
          <Button onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JsonEditorModal;